import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DirectionsRunRoundedIcon from "@mui/icons-material/DirectionsRunRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import LocalPhoneRoundedIcon from "@mui/icons-material/LocalPhoneRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import MedicalServicesRoundedIcon from "@mui/icons-material/MedicalServicesRounded";
import SelfImprovementRoundedIcon from "@mui/icons-material/SelfImprovementRounded";
import SpaRoundedIcon from "@mui/icons-material/SpaRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import SEOHead from "../components/SEOHead";
import Reveal from "../components/Reveal";
import { clinic, openingHoursSpecification } from "../constants/clinic";

const sectionTitleSx = {
  fontSize: { xs: "1.85rem", sm: "2.25rem", md: "2.75rem" },
  mb: 2,
};

const concerns = [
  {
    icon: <SelfImprovementRoundedIcon />,
    title: "腰の痛み",
    text: "立つ・歩く・座るときの腰のつらさを、体の動きから丁寧に確認します。",
  },
  {
    icon: <FavoriteRoundedIcon />,
    title: "肩の痛み・五十肩",
    text: "腕が上げづらい、着替えがつらいなど、日常の困りごとに合わせて施術します。",
  },
  {
    icon: <DirectionsRunRoundedIcon />,
    title: "捻挫・打撲・スポーツ外傷",
    text: "急なけがからスポーツ時の不調まで、状態を見極めて回復を支えます。",
  },
  {
    icon: <TuneRoundedIcon />,
    title: "姿勢・体のバランス",
    text: "背骨や全身のバランスを確認し、動きやすい体づくりをお手伝いします。",
  },
];

const strengths = [
  {
    number: "01",
    title: "36年の経験",
    text: "柔道整復師・鍼灸師として培った経験を活かし、一人ひとりの体の状態に合わせて丁寧に対応します。",
  },
  {
    number: "02",
    title: "施術と運動療法",
    text: "最新の施術法を提供し、必要に応じてご自宅でも続けやすい運動を分かりやすくお伝えします。",
  },
  {
    number: "03",
    title: "分かりやすい説明",
    text: "今の体がどのような状態か、これから何をするのか。専門用語に頼らず、納得できる説明を大切にします。",
  },
];

const Home = () => {
  const homeStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "瀬戸市の接骨院｜わかば接骨院",
    description:
      "愛知県瀬戸市共栄通のわかば接骨院。腰の痛み・肩の痛み、運動療法に対応しています。",
    url: `${clinic.domain}/`,
    mainEntity: {
      "@type": "MedicalClinic",
      name: clinic.name,
      url: clinic.domain,
      image: `${clinic.domain}/picture_of_outside.webp`,
      telephone: clinic.phone,
      description:
        "愛知県瀬戸市共栄通の接骨院。腰の痛み・肩の痛み、捻挫・打撲、運動療法に対応。",
      address: {
        "@type": "PostalAddress",
        streetAddress: "共栄通3-5",
        addressLocality: "瀬戸市",
        addressRegion: "愛知県",
        postalCode: clinic.postalCode,
        addressCountry: "JP",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: 35.2213662,
        longitude: 137.0798178,
      },
      openingHoursSpecification,
      areaServed: { "@type": "City", name: "瀬戸市" },
      availableService: [
        { "@type": "MedicalService", name: "腰痛施術" },
        { "@type": "MedicalService", name: "肩の痛み・五十肩の施術" },
        { "@type": "MedicalService", name: "運動療法" },
        { "@type": "MedicalService", name: "鍼灸施術" },
      ],
    },
  };

  return (
    <>
      <SEOHead
        title="瀬戸市の接骨院｜腰の痛み・肩の痛みなら"
        description="瀬戸市共栄通のわかば接骨院。腰の痛み・肩の痛み、捻挫・打撲、運動療法に対応。新瀬戸駅から徒歩10分、専用駐車場9台。"
        keywords="わかば接骨院,瀬戸市 接骨院,新瀬戸 接骨院,瀬戸市 腰痛,瀬戸市 五十肩,瀬戸市 運動療法"
        canonical="/"
        ogImage="/og.png"
        structuredData={homeStructuredData}
      />

      <Box component="section" sx={{ position: "relative", overflow: "hidden", pt: { xs: 5, md: 8 }, pb: { xs: 7, md: 11 } }}>
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            width: 520,
            height: 520,
            borderRadius: "50%",
            bgcolor: "primary.light",
            opacity: 0.62,
            right: { xs: -340, md: -160 },
            top: { xs: -230, md: -260 },
            filter: "blur(2px)",
          }}
        />
        <Container maxWidth="lg" sx={{ position: "relative" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.02fr) minmax(420px, .98fr)" },
              gap: { xs: 5, md: 8 },
              alignItems: "center",
            }}
          >
            <Reveal>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
                <Box sx={{ width: 34, height: 1, bgcolor: "primary.main" }} />
                <Typography sx={{ color: "primary.dark", fontWeight: 700, letterSpacing: ".09em", fontSize: { xs: ".78rem", sm: ".9rem" } }}>
                  瀬戸市共栄通・地域に寄り添って36年
                </Typography>
              </Stack>
              <Typography
                component="h1"
                variant="h1"
                sx={{ fontSize: { xs: "2.45rem", sm: "3.25rem", md: "4rem" }, mb: 2.5 }}
              >
                痛みに向き合い、
                <Box component="span" sx={{ display: "block", color: "primary.dark" }}>
                  また動ける毎日へ。
                </Box>
              </Typography>
              <Typography sx={{ fontSize: { xs: "1.04rem", sm: "1.13rem" }, color: "text.secondary", maxWidth: 620, mb: 3.5 }}>
                腰の痛み・肩の痛みを中心に、体の状態や暮らしに合わせた施術と運動療法を。
                経験豊富な院長が、はじめから最後まで丁寧に対応します。
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
                <Button
                  component="a"
                  href={clinic.phoneHref}
                  variant="contained"
                  size="large"
                  startIcon={<LocalPhoneRoundedIcon />}
                >
                  電話で予約する
                </Button>
                <Button
                  component={RouterLink}
                  to="/services"
                  variant="outlined"
                  size="large"
                  endIcon={<ArrowForwardRoundedIcon />}
                >
                  施術内容を見る
                </Button>
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, sm: 2.5 }} sx={{ mt: 3 }}>
                {["柔道整復師・鍼灸師", "専用駐車場9台", "新瀬戸駅 徒歩10分"].map((item) => (
                  <Stack key={item} direction="row" spacing={0.7} alignItems="center">
                    <CheckCircleRoundedIcon sx={{ color: "primary.dark", fontSize: 19 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{item}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Reveal>

            <Reveal delay={120}>
              <Box sx={{ position: "relative", px: { xs: 1.5, sm: 4, md: 0 } }}>
                <Box
                  component="img"
                  src="/picture_of_outside.webp"
                  alt="瀬戸市共栄通にある、わかば接骨院の外観"
                  fetchPriority="high"
                  sx={{
                    width: "100%",
                    height: { xs: 330, sm: 450, md: 530 },
                    objectFit: "cover",
                    borderRadius: { xs: "120px 24px 80px 24px", md: "180px 28px 120px 28px" },
                    boxShadow: "0 28px 80px rgba(111, 58, 35, .2)",
                  }}
                />
                <Paper
                  elevation={0}
                  sx={{
                    position: "absolute",
                    left: { xs: 0, sm: 14, md: -30 },
                    bottom: { xs: 18, sm: 26, md: 42 },
                    p: 2.2,
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: "0 18px 45px rgba(111, 58, 35, .18)",
                  }}
                >
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    <Box sx={{ width: 42, height: 42, borderRadius: "50%", bgcolor: "primary.light", color: "primary.dark", display: "grid", placeItems: "center" }}>
                      <VerifiedRoundedIcon />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">院長</Typography>
                      <Typography sx={{ fontWeight: 700 }}>住田 雅美</Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Box>
            </Reveal>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: { xs: -2, md: -4 }, position: "relative", zIndex: 2 }}>
        <Reveal>
          <Paper
            elevation={0}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1.25fr 1fr 1fr" },
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 18px 55px rgba(111, 58, 35, .1)",
              overflow: "hidden",
            }}
          >
            {[
              {
                icon: <AccessTimeRoundedIcon />,
                label: "診療時間",
                value: "午前9時から12時 / 午後4時から8時",
                note: "水曜・土曜は午前のみ　日曜・祝日は休診",
              },
              {
                icon: <LocationOnRoundedIcon />,
                label: "所在地",
                value: "瀬戸市共栄通3-5",
                note: "新瀬戸駅から徒歩10分",
              },
              {
                icon: <LocalPhoneRoundedIcon />,
                label: "ご予約・ご相談",
                value: clinic.phone,
                note: "お電話で承ります",
              },
            ].map((item, index) => (
              <Stack
                key={item.label}
                direction="row"
                spacing={1.5}
                sx={{
                  p: { xs: 2.5, sm: 3 },
                  borderTop: { xs: index ? "1px solid" : "none", md: "none" },
                  borderLeft: { xs: "none", md: index ? "1px solid" : "none" },
                  borderColor: "divider",
                  alignItems: "flex-start",
                }}
              >
                <Box sx={{ color: "primary.dark", mt: 0.25 }}>{item.icon}</Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>{item.label}</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: { xs: "1rem", sm: "1.06rem" }, my: 0.2 }}>{item.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{item.note}</Typography>
                </Box>
              </Stack>
            ))}
          </Paper>
        </Reveal>
      </Container>

      <Box component="section" sx={{ py: { xs: 9, md: 14 } }}>
        <Container maxWidth="lg">
          <Reveal sx={{ textAlign: "center", maxWidth: 740, mx: "auto", mb: { xs: 5, md: 7 } }}>
            <Chip label="こんなお悩みに" color="primary" variant="outlined" sx={{ mb: 2.5, fontWeight: 700 }} />
            <Typography component="h2" variant="h2" sx={sectionTitleSx}>
              毎日の「つらい」を、<br />そのままにしていませんか？
            </Typography>
            <Typography color="text.secondary">
              痛み方や動かしづらさは人それぞれです。まずは今のお困りごとを丁寧に確認し、
              体の状態に合わせた施術をご提案します。
            </Typography>
          </Reveal>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }, gap: 2.5 }}>
            {concerns.map((item, index) => (
              <Reveal key={item.title} delay={index * 70}>
                <Paper
                  elevation={0}
                  sx={{
                    height: "100%",
                    p: 3.2,
                    border: "1px solid",
                    borderColor: "divider",
                    transition: "transform .25s ease, box-shadow .25s ease, border-color .25s ease",
                    "&:hover": { transform: "translateY(-7px)", boxShadow: "0 20px 45px rgba(111, 58, 35, .12)", borderColor: "primary.light" },
                  }}
                >
                  <Box sx={{ width: 54, height: 54, borderRadius: 3, bgcolor: "primary.light", color: "primary.dark", display: "grid", placeItems: "center", mb: 2.3, "& svg": { fontSize: 29 } }}>
                    {item.icon}
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>{item.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{item.text}</Typography>
                </Paper>
              </Reveal>
            ))}
          </Box>
          <Box sx={{ textAlign: "center", mt: 4 }}>
            <Button component={RouterLink} to="/services" endIcon={<ArrowForwardRoundedIcon />}>
              施術内容を詳しく見る
            </Button>
          </Box>
        </Container>
      </Box>

      <Box component="section" sx={{ bgcolor: "#F5F5F4", py: { xs: 9, md: 14 }, overflow: "hidden" }}>
        <Container maxWidth="lg">
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: ".88fr 1.12fr" }, gap: { xs: 5, md: 9 }, alignItems: "center" }}>
            <Reveal>
              <Box sx={{ position: "relative", maxWidth: 520, mx: { xs: "auto", md: 0 } }}>
                <Box
                  component="img"
                  src="/picture_of_doctor.webp"
                  alt="わかば接骨院 院長 住田雅美"
                  loading="lazy"
                  sx={{ width: "100%", height: { xs: 390, sm: 520 }, objectFit: "cover", objectPosition: "center 28%", borderRadius: "28px 120px 28px 28px" }}
                />
                <Box sx={{ position: "absolute", right: -16, bottom: 24, bgcolor: "primary.main", color: "primary.contrastText", borderRadius: 3, px: 2.5, py: 2, boxShadow: "0 16px 35px rgba(41, 37, 36, .16)" }}>
                  <Typography sx={{ fontFamily: '"Noto Serif JP", serif', fontSize: "1.7rem", fontWeight: 700, lineHeight: 1 }}>36年</Typography>
                  <Typography variant="caption">地域で培った経験</Typography>
                </Box>
              </Box>
            </Reveal>
            <Reveal delay={100}>
              <Chip label="院長からのごあいさつ" sx={{ mb: 2.5, bgcolor: "white", color: "primary.dark", fontWeight: 700 }} />
              <Typography component="h2" variant="h2" sx={sectionTitleSx}>
                不安な気持ちまで、<br />置いて帰れる場所に。
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                体の痛みがあると、普段は何気なくできていたことまで不安になります。
                だからこそ、わかば接骨院では施術だけでなく、分かりやすい説明と話しやすい空気を大切にしています。
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                柔道整復師・鍼灸師としての経験を活かし、今の状態とこれからの見通しを一緒に確認しながら進めます。
                どんな小さなお悩みでも、気兼ねなくご相談ください。
              </Typography>
              <Typography variant="h6" sx={{ fontFamily: '"Noto Serif JP", serif' }}>院長　住田 雅美</Typography>
              <Button component={RouterLink} to="/about" endIcon={<ArrowForwardRoundedIcon />} sx={{ mt: 2.5 }}>
                当院について
              </Button>
            </Reveal>
          </Box>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 9, md: 14 } }}>
        <Container maxWidth="lg">
          <Reveal sx={{ display: { md: "flex" }, justifyContent: "space-between", alignItems: "flex-end", mb: { xs: 5, md: 7 } }}>
            <Box>
              <Typography sx={{ color: "primary.dark", fontWeight: 700, letterSpacing: ".1em", mb: 1.2 }}>WAKABA’S APPROACH</Typography>
              <Typography component="h2" variant="h2" sx={{ ...sectionTitleSx, mb: 0 }}>
                わかば接骨院が<br />大切にしていること
              </Typography>
            </Box>
            <Typography color="text.secondary" sx={{ maxWidth: 450, mt: { xs: 2, md: 0 } }}>
              長く地域の皆さまと向き合ってきたからこそ、誠実に、分かりやすく、続けやすい施術を心がけています。
            </Typography>
          </Reveal>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2.5 }}>
            {strengths.map((item, index) => (
              <Reveal key={item.number} delay={index * 90}>
                <Paper elevation={0} sx={{ height: "100%", p: { xs: 3.5, md: 4 }, border: "1px solid", borderColor: "divider", position: "relative", overflow: "hidden" }}>
                  <Typography aria-hidden="true" sx={{ position: "absolute", right: 20, top: 8, fontFamily: '"Noto Serif JP", serif', fontSize: "4.5rem", fontWeight: 700, color: "primary.light", lineHeight: 1 }}>
                    {item.number}
                  </Typography>
                  <Box sx={{ width: 42, height: 3, bgcolor: "primary.main", mb: 3, position: "relative" }} />
                  <Typography variant="h5" sx={{ mb: 1.5, position: "relative" }}>{item.title}</Typography>
                  <Typography color="text.secondary" sx={{ position: "relative" }}>{item.text}</Typography>
                </Paper>
              </Reveal>
            ))}
          </Box>
        </Container>
      </Box>

      <Box component="section" sx={{ pb: { xs: 9, md: 14 } }}>
        <Container maxWidth="lg">
          <Reveal sx={{ textAlign: "center", mb: { xs: 5, md: 7 } }}>
            <Chip label="初めての方へ" variant="outlined" color="primary" sx={{ mb: 2.5, fontWeight: 700 }} />
            <Typography component="h2" variant="h2" sx={sectionTitleSx}>ご来院から施術まで</Typography>
            <Typography color="text.secondary">初めての方にも、順を追って分かりやすくご案内します。</Typography>
          </Reveal>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: { xs: 2, md: 0 } }}>
            {[
              ["01", "受付・問診", "お困りのことや生活の中で気になる動きを丁寧に伺います。"],
              ["02", "状態の確認と説明", "体の動きや状態を確認し、施術の方針を分かりやすくお伝えします。"],
              ["03", "一人ひとりに合う施術", "無理のない施術を行い、必要に応じて日常の運動もご案内します。"],
            ].map(([number, title, text], index) => (
              <Reveal key={number} delay={index * 80}>
                <Box sx={{ px: { xs: 3, md: 4 }, py: 3, borderLeft: { xs: "3px solid", md: index ? "1px solid" : "none" }, borderColor: { xs: "primary.main", md: "divider" } }}>
                  <Typography sx={{ color: "primary.dark", fontWeight: 700, letterSpacing: ".12em", mb: 1.2 }}>STEP {number}</Typography>
                  <Typography variant="h5" sx={{ mb: 1 }}>{title}</Typography>
                  <Typography variant="body2" color="text.secondary">{text}</Typography>
                </Box>
              </Reveal>
            ))}
          </Box>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="md">
          <Reveal>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, sm: 6, md: 7 },
                textAlign: "center",
                bgcolor: "primary.light",
                border: "1px solid",
                borderColor: "divider",
                color: "text.primary",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <SpaRoundedIcon aria-hidden="true" sx={{ position: "absolute", fontSize: 220, right: -40, bottom: -65, opacity: .06 }} />
              <MedicalServicesRoundedIcon sx={{ fontSize: 38, color: "primary.dark", mb: 2 }} />
              <Typography component="h2" variant="h2" sx={{ fontSize: { xs: "1.9rem", sm: "2.4rem" }, mb: 1.5, color: "text.primary" }}>
                まずは、お気軽にご相談ください
              </Typography>
              <Typography sx={{ color: "rgba(41,37,36,.78)", mb: 3.5 }}>
                「この症状でも大丈夫？」というご相談も、お電話で承ります。
              </Typography>
              <Button component="a" href={clinic.phoneHref} variant="contained" size="large" startIcon={<LocalPhoneRoundedIcon />} sx={{ fontSize: { xs: "1.03rem", sm: "1.18rem" }, bgcolor: "primary.main", color: "white", "&:hover": { bgcolor: "primary.dark" } }}>
                {clinic.phone}
              </Button>
              <Typography variant="body2" sx={{ mt: 2.5, color: "rgba(41,37,36,.74)" }}>
                午前9時から12時 / 午後4時から8時<br />
                水曜・土曜は午前のみ　日曜・祝日は休診
              </Typography>
            </Paper>
          </Reveal>
        </Container>
      </Box>
    </>
  );
};

export default Home;
